import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getAllCreditSummaries, CreditSummaryResponse } from '../utils/creditService';

interface PendingTransactionData {
  creditLineId: number;
  pending_repayments: number;
  pending_fee_repayments: number;
  pending_withdraws: number;
}

interface PendingTransactionContextType {
  pendingTransactions: PendingTransactionData[];
  isPolling: boolean;
  lastUpdated: Date | null;
  startPolling: () => void;
  stopPolling: () => void;
  getPendingDataForCreditLine: (creditLineId: number) => PendingTransactionData | null;
}

const PendingTransactionContext = createContext<PendingTransactionContextType | undefined>(undefined);

export const usePendingTransactions = () => {
  const context = useContext(PendingTransactionContext);
  if (context === undefined) {
    throw new Error('usePendingTransactions must be used within a PendingTransactionProvider');
  }
  return context;
};

interface PendingTransactionProviderProps {
  children: React.ReactNode;
}

export const PendingTransactionProvider: React.FC<PendingTransactionProviderProps> = ({ children }) => {
  const [pendingTransactions, setPendingTransactions] = useState<PendingTransactionData[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousDataRef = useRef<string>('');
  const errorCountRef = useRef(0);

  const fetchPendingTransactions = useCallback(async () => {
    try {
      const creditSummaries = await getAllCreditSummaries();
      
      const pendingData: PendingTransactionData[] = creditSummaries.map((summary: CreditSummaryResponse) => ({
        creditLineId: summary.available_balances.id,
        pending_repayments: summary.available_balances.pending_repayments || 0,
        pending_fee_repayments: summary.available_balances.pending_fee_repayments || 0,
        pending_withdraws: summary.available_balances.pending_withdraws || 0,
      }));


      // Only update state if data has actually changed (optimization)
      const currentDataString = JSON.stringify(pendingData);
      if (currentDataString !== previousDataRef.current) {
        setPendingTransactions(pendingData);
        setLastUpdated(new Date());
        previousDataRef.current = currentDataString;
      }
      
      // Reset error count on successful fetch
      errorCountRef.current = 0;
    } catch (error) {
      console.error('Error fetching pending transactions:', error);
      errorCountRef.current += 1;
      
      // Stop polling after 3 consecutive errors to prevent spam
      if (errorCountRef.current >= 3) {
        console.warn('Stopping pending transaction polling due to consecutive errors');
        setIsPolling(false);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }
  }, []);

  const startPolling = useCallback(() => {
    // Always clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Use callback form to avoid dependency on isPolling state
    setIsPolling(currentIsPolling => {
      if (currentIsPolling) return currentIsPolling; // Already polling, no change
      
      // Start polling
      fetchPendingTransactions();
      intervalRef.current = setInterval(fetchPendingTransactions, 5000);
      
      return true; // Set isPolling to true
    });
  }, [fetchPendingTransactions]);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const getPendingDataForCreditLine = useCallback((creditLineId: number): PendingTransactionData | null => {
    return pendingTransactions.find(data => data.creditLineId === creditLineId) || null;
  }, [pendingTransactions]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const contextValue: PendingTransactionContextType = {
    pendingTransactions,
    isPolling,
    lastUpdated,
    startPolling,
    stopPolling,
    getPendingDataForCreditLine,
  };

  return (
    <PendingTransactionContext.Provider value={contextValue}>
      {children}
    </PendingTransactionContext.Provider>
  );
};
